import { UserType, isDefined, schCreateStripeSubscriptionRequest } from '@aloft/toolkit-shared';
import { Button, Input, SimpleInput } from '@aloft/toolkit-sui';
import { useQueryClient } from '@tanstack/react-query';

import { Controller, SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { useUpdateToolkitAccount } from '../../../../controller/hooks/useUpdateToolkitAccount.ts';
import { useCreateStripeSubscription } from '../../../../hooks/api/toolkitComponents.ts';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { useZodForm } from '../../../../hooks/useZodForm.ts';
import { completeOnboardingForm } from '../../../../redux/features/accountSlice.ts';
import { selectAccount } from '../../../../redux/features/selectors/accountSelectors.ts';
import { featuresSelectors } from '../../../../redux/features/selectors/featuresSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { getConfig } from '../../../../utils/getConfig.ts';
import { schOnboardingForm } from '../../consts.ts';
import { FormGroup } from './FormGroup.tsx';
import { FormRow } from './FormRow.tsx';
import { OnboardingFormStepProps } from './types.ts';

const { selectShouldAllowTrialWithoutPayment } = featuresSelectors;

const monthlyPriceID = getConfig('STRIPE_PRICE_MONTHLY');

const getSubmitButtonText = (
  allowTrialWithoutPayment: boolean,
  mcKissockOffer: OnboardingFormStepProps['mcKissockOffer'],
  userType?: UserType,
): string => {
  if (!allowTrialWithoutPayment) return 'Next: Select a plan';
  if (mcKissockOffer === 'bundle') return 'Start 60-Day Free Trial and Unlock 15% Off';
  if (mcKissockOffer === 'trial') return 'Start 60-Day Free Trial';
  if (userType === 'appraisify') return 'Get Started with Appraisify';
  return 'Start Free Trial';
};

type Props = {
  referralCode: OnboardingFormStepProps['referralCode'];
  mcKissockOffer: OnboardingFormStepProps['mcKissockOffer'];
  onFinish: OnboardingFormStepProps['onFinish'];
};

export const Form = ({ mcKissockOffer, onFinish }: Props) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const toast = useToaster();
  const updateAccount = useUpdateToolkitAccount({
    onSuccess: (result) => {
      queryClient
        .invalidateQueries({
          queryKey: ['api', 'v1', 'account'],
        })
        .catch(console.error);
      dispatch(completeOnboardingForm(result));
    },
  });
  const createStripeSubscription = useCreateStripeSubscription({
    onError: (error) => {
      console.error('Could not create Stripe subscription.', JSON.stringify(error, null, 2));
      toast({
        id: 'create-subscription-error',
        type: 'error',
        title: 'Could not start free trial',
        body: 'There was an error starting your free trial. Please try again.',
      });
    },
  });
  const allowTrialWithoutPayment = useAppSelector((state) =>
    selectShouldAllowTrialWithoutPayment(state, null),
  );

  const hasMcKissockOffer = isDefined(mcKissockOffer);
  const account = useAppSelector((state) => selectAccount(state));

  const form = useZodForm({
    schema: schOnboardingForm,
    defaultValues: {
      version: 1,
      firstName: '',
      lastName: '',
      phone: '',
      mcKissockData: hasMcKissockOffer
        ? {
            email: '',
            type: mcKissockOffer,
          }
        : undefined,
    },
  });

  const onSubmit: SubmitHandler<z.infer<typeof schOnboardingForm>> = async (data) => {
    await updateAccount.mutateAsync({
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      address: data.address,
      isArchived: false,
      isOnboardingComplete: true,
      mlsIDs: [],
      userDefinedRole: undefined,
      licenseID: undefined,
      company: undefined,
      licenseState: undefined,
      mcKissockData: data.mcKissockData,
    });

    if (allowTrialWithoutPayment) {
      await createStripeSubscription.mutateAsync({
        body: schCreateStripeSubscriptionRequest.parse({
          priceID: monthlyPriceID, // this can be changed when the user adds their payment method
          addTrial: hasMcKissockOffer ? undefined : true,
          mcKissockOffer: hasMcKissockOffer ? mcKissockOffer : undefined,
        }),
      });
    }

    onFinish();
  };

  const onInvalid: SubmitErrorHandler<z.infer<typeof schOnboardingForm>> = (errors) => {
    console.warn(errors);
  };

  const { errors, isSubmitting } = form.formState;

  const isSubmitDisabled =
    isSubmitting ||
    updateAccount.isPending ||
    createStripeSubscription.isPending ||
    updateAccount.isError;

  const isSubmitLoading =
    isSubmitting || updateAccount.isPending || createStripeSubscription.isPending;

  const submitButtonText = getSubmitButtonText(
    allowTrialWithoutPayment,
    mcKissockOffer,
    account.userType,
  );

  return (
    <form
      {...form}
      style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '36px' }}
    >
      <FormGroup>
        <FormRow>
          <Controller
            name="firstName"
            control={form.control}
            render={({ field }) => (
              <SimpleInput {...field} label="First name" invalid={!!errors.firstName} required />
            )}
          />
          <Controller
            name="lastName"
            control={form.control}
            render={({ field }) => (
              <SimpleInput {...field} label="Last name" invalid={!!errors.lastName} required />
            )}
          />
        </FormRow>
        <FormRow>
          <Controller
            name="phone"
            control={form.control}
            render={({ field }) => (
              <Input
                type="phone"
                label="Phone number"
                {...field}
                isInvalid={!!errors.phone}
                isRequired
                onClear={() => field.onChange('')}
              />
            )}
          />
          <Controller
            name="address.PostalCode"
            control={form.control}
            render={({ field }) => (
              <SimpleInput
                {...field}
                label="Zip code"
                invalid={!!errors.address?.PostalCode}
                required
              />
            )}
          />
        </FormRow>
        {hasMcKissockOffer && (
          <Controller
            name="mcKissockData.email"
            control={form.control}
            render={({ field }) => (
              <SimpleInput
                {...field}
                type="text"
                label="McKissock email address"
                invalid={!!errors.mcKissockData?.email}
                required
              />
            )}
          />
        )}
      </FormGroup>
      <Button
        type="submit"
        text={submitButtonText}
        disabled={isSubmitDisabled}
        isLoading={isSubmitLoading}
        onClick={form.handleSubmit(onSubmit, onInvalid)}
      />
    </form>
  );
};
